import { useState, useEffect } from "react";
import { Form, useActionData, useNavigation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { useAuth } from "../../hooks/useAuth";
import type { ActionResult } from "../../routes/actions";
import { generateSlug } from "../../utils";
import "./CreateOrganizationPage.css";

export default function CreateOrganizationPage() {
  const { t } = useTranslation();
  const { refetchUser } = useAuth();
  const navigation = useNavigation();
  const actionData = useActionData() as ActionResult | undefined;
  const isSubmitting = navigation.state === "submitting";

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);

  useEffect(() => {
    if (autoGenerateSlug && name) {
      setSlug(generateSlug(name));
    }
  }, [name, autoGenerateSlug]);

  useEffect(() => {
    if (!actionData) return;

    if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  useEffect(() => {
    if (navigation.state === "loading" && actionData === undefined) {
      refetchUser();
    }
  }, [navigation.state, actionData, refetchUser]);

  return (
    <>
      <Header />
      <div className="container">
        <div className="create-org-container">
          <div className="create-org-header">
            <h1>{t("organizations.create.title")}</h1>
            <p>{t("organizations.create.subtitle")}</p>
          </div>

          <Form method="post" className="create-org-form">
            <div className={`form-group ${actionData?.fieldErrors?.name ? "error" : ""}`}>
              <label htmlFor="name">{t("organizations.create.nameLabel")}</label>
              <input
                id="name"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("organizations.create.namePlaceholder")}
                disabled={isSubmitting}
              />
              {actionData?.fieldErrors?.name && (
                <span className="form-error">{actionData.fieldErrors.name}</span>
              )}
            </div>

            <div className={`form-group ${actionData?.fieldErrors?.slug ? "error" : ""}`}>
              <label htmlFor="slug">
                {t("organizations.create.slugLabel")}
                <button
                  type="button"
                  className="toggle-button"
                  onClick={() => setAutoGenerateSlug(!autoGenerateSlug)}
                >
                  {autoGenerateSlug
                    ? t("organizations.create.slugAuto")
                    : t("organizations.create.slugCustom")}
                </button>
              </label>
              <input
                id="slug"
                name="slug"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={t("organizations.create.slugPlaceholder")}
                disabled={isSubmitting || autoGenerateSlug}
                className={autoGenerateSlug ? "disabled-input" : ""}
              />
              {actionData?.fieldErrors?.slug && (
                <span className="form-error">{actionData.fieldErrors.slug}</span>
              )}
              <small className="help-text">{t("organizations.create.slugHelp")}</small>
            </div>

            <div className="form-actions">
              <Link to="/organizations" className="btn-secondary">
                {t("common.cancel")}
              </Link>
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span> {t("organizations.create.submitting")}
                  </>
                ) : (
                  t("organizations.create.submitButton")
                )}
              </button>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
}
